import mem from 'mem';
import { TruvityClient } from './Client';
import { fetcher } from './core';
import { LinkedFile, VcDecorator, VpDecorator } from './documents';
import { Claims, Ctor } from './documents/types';
import { createFetcherWithMiddlewares, Middleware } from './middlewares';

type ExtendedTruvityClientOptions = TruvityClient.Options & {
    fetcherMiddlewares?: Middleware<any>[];
};

export class ExtendedTruvityClient extends TruvityClient {
    constructor({ fetcherMiddlewares, fetcher: customFetcher, ...options }: ExtendedTruvityClientOptions = {}) {
        super({
            ...options,
            fetcher: fetcherMiddlewares
                ? createFetcherWithMiddlewares(customFetcher ?? fetcher, fetcherMiddlewares)
                : customFetcher,
        });
    }

    public createVcDecorator<T extends Claims>(model: Ctor<T>): VcDecorator<T> {
        return new VcDecorator(this, model);
    }

    public createVpDecorator(): VpDecorator {
        return new VpDecorator(this);
    }

    @mem.decorator()
    public async tenantId(): Promise<string> {
        const id = (await this.dids.didDocumentSelfGet()).id;
        return id.substring(id.lastIndexOf(':') + 1);
    }

    public async createLinkedFile(
        file: ArrayBufferView | ArrayBuffer | ReadableStream,
        params?: { filename?: string; annotations?: Record<string, string>; labels?: Record<string, string> },
    ): Promise<LinkedFile> {
        const { blobId, uploadUri: initialUploadUri } = await this.files.fileUpload();

        await fetch(initialUploadUri, {
            method: 'PUT',
            body: file,
        });

        const resource = await this.files.fileCreate({
            blobId,
            ...params,
            data: {
                filename: params?.filename,
            },
        });

        return new LinkedFile(this, resource.id, resource.data.digestMultibase);
    }
}
